/**
 * Recruitment-Pipeline Events Logger (SE-5508)
 *
 * Handles logging of the SE-6071 pipeline epic's product events to PostHog:
 * pipeline lifecycle, card lifecycle, the Suggested-players surface, automation
 * rules, the Review queue, the view toggle, load time, and the Manager+ tier
 * gate.
 *
 * Every method is a thin pass-through to {@link sendAnalyticsEvent}, which is
 * the ONLY analytics entry point in this app. Routing through it is what gives
 * each pipeline event the standard tagging convention for free: the
 * `environment` / `$app_version` / `org_id` / `org_name` super-properties
 * registered by `setPostHogInstance`, and the `distinct_id` established by
 * `identifyUser`. Calling `posthog.capture()` directly anywhere in the pipeline
 * feature would bypass both the super-properties and the strict Yup validation,
 * producing events that cannot be separated from production traffic — all
 * environments share one PostHog project and key.
 * @module features/analytics/utils/pipelineEventLogger
 */
import { sendAnalyticsEvent } from "./posthogSender";
import { ANALYTICS_EVENTS } from "../constants";
import type {
  IPipelineAnalyticsService,
  TCardAddedParams,
  TCardMovedParams,
  TCardRemovedParams,
  TPipelineCreatedParams,
  TPipelineLoadTimeParams,
  TPipelineStageEditedParams,
  TReviewItemResolvedParams,
  TRuleCreatedParams,
  TSuggestionActedParams,
  TTierBlockRenderParams,
  TViewToggledParams,
} from "../types";

/**
 * Pipeline events analytics logger.
 * @example
 * ```typescript
 * import { AnalyticsTracker } from '@/features/analytics';
 *
 * AnalyticsTracker.pipeline.logCardMoved({
 *   from: 'phase-1', to: 'phase-2', method: 'drag',
 *   sensitive: false, outcome: null, board_id: 'board-1',
 * });
 * ```
 */
export const pipelineEventLogger: IPipelineAnalyticsService = {
  /**
   * Logs a pipeline created event.
   * @param params - Pipeline created parameters
   */
  logPipelineCreated: async (params: TPipelineCreatedParams): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.PIPELINE.CREATED, params);
  },

  /**
   * Logs a pipeline stage edited event.
   * @param params - Stage edited parameters
   */
  logStageEdited: async (params: TPipelineStageEditedParams): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.PIPELINE.STAGE_EDITED, params);
  },

  /**
   * Logs a card added event.
   * @param params - Card added parameters
   */
  logCardAdded: async (params: TCardAddedParams): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.PIPELINE.CARD_ADDED, params);
  },

  /**
   * Logs a card moved event.
   * @param params - Card moved parameters
   */
  logCardMoved: async (params: TCardMovedParams): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.PIPELINE.CARD_MOVED, params);
  },

  /**
   * Logs a card removed event.
   * @param params - Card removed parameters
   */
  logCardRemoved: async (params: TCardRemovedParams): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.PIPELINE.CARD_REMOVED, params);
  },

  /**
   * Logs a suggestion acted event.
   * @param params - Suggestion acted parameters
   */
  logSuggestionActed: async (params: TSuggestionActedParams): Promise<void> => {
    await sendAnalyticsEvent(
      ANALYTICS_EVENTS.PIPELINE.SUGGESTION_ACTED,
      params
    );
  },

  /**
   * Logs a rule created event.
   * @param params - Rule created parameters
   */
  logRuleCreated: async (params: TRuleCreatedParams): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.PIPELINE.RULE_CREATED, params);
  },

  /**
   * Logs a review item resolved event.
   * @param params - Review item resolved parameters
   */
  logReviewItemResolved: async (
    params: TReviewItemResolvedParams
  ): Promise<void> => {
    await sendAnalyticsEvent(
      ANALYTICS_EVENTS.PIPELINE.REVIEW_ITEM_RESOLVED,
      params
    );
  },

  /**
   * Logs a view toggled event.
   * @param params - View toggled parameters
   */
  logViewToggled: async (params: TViewToggledParams): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.PIPELINE.VIEW_TOGGLED, params);
  },

  /**
   * Logs a pipeline load time event.
   * @param params - Pipeline load time parameters
   */
  logLoadTime: async (params: TPipelineLoadTimeParams): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.PIPELINE.LOAD_TIME_MS, params);
  },

  /**
   * Logs a tier block render event.
   * @param params - Tier block render parameters
   */
  logTierBlockRender: async (params: TTierBlockRenderParams): Promise<void> => {
    await sendAnalyticsEvent(
      ANALYTICS_EVENTS.PIPELINE.TIER_BLOCK_RENDER,
      params
    );
  },
};
